import { useTranslation } from "react-i18next";
import { Download, FileCheck2, FileUp, Loader2 } from "lucide-react";

export interface ConsentCopySummary {
  id: string;
  consent_id: string;
  source: string;
  content_type: string;
  created_at: string;
  document_hash?: string | null;
}

interface ConsentCopiesListProps {
  copies: ConsentCopySummary[];
  onDownload: (copyId: string) => void;
  downloadingId?: string | null;
  /** Hidden when there is nothing to say yet — an empty consent is not a gap. */
  showWhenEmpty?: boolean;
}

/**
 * Every stored copy of a patient's consent, newest first.
 *
 * Signing again adds a copy instead of replacing the one before it, and
 * withdrawing keeps them all, so this is a history rather than a single
 * download: which copy proves which consent depends on when it was signed.
 *
 * Shared by the patient's portal and the therapist's record — the same facts,
 * fetched by each side from its own API.
 */
export default function ConsentCopiesList({
  copies,
  onDownload,
  downloadingId = null,
  showWhenEmpty = false,
}: ConsentCopiesListProps) {
  const { t } = useTranslation();

  if (copies.length === 0 && !showWhenEmpty) return null;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
        {t("consentCopies.title")}
      </h3>
      <p className="text-xs text-content-tertiary">{t("consentCopies.retentionHint")}</p>

      {copies.length === 0 ? (
        <p className="text-xs text-content-tertiary">{t("consentCopies.empty")}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
          {copies.map((copy) => {
            const signed = copy.source === "signed";
            return (
              <li key={copy.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  {signed ? (
                    <FileCheck2 className="w-4 h-4 text-brand-500 shrink-0" />
                  ) : (
                    <FileUp className="w-4 h-4 text-content-tertiary shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-content-primary truncate">
                      {signed ? t("consentCopies.signed") : t("consentCopies.uploaded")}
                    </p>
                    <p className="text-xs text-content-tertiary">
                      {new Date(copy.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onDownload(copy.id)}
                  disabled={downloadingId === copy.id}
                  aria-label={t("consentCopies.download")}
                  className="flex items-center gap-1.5 min-h-11 px-2 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
                >
                  {downloadingId === copy.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  {t("consentCopies.download")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
